import React from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSessionId } = useAuth();
  const hasProcessed = React.useRef(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    exchangeSessionId(sid)
      .then((u) => {
        // Honor `next` query param if present (e.g., ?next=/my-bookings)
        const search = new URLSearchParams(window.location.search);
        const next = search.get("next");
        if (next && next.startsWith("/")) {
          navigate(next, { replace: true, state: { user: u } });
          return;
        }
        if (u.is_admin) navigate("/admin", { replace: true, state: { user: u } });
        else navigate("/my-bookings", { replace: true, state: { user: u } });
      })
      .catch((e) => {
        console.error(e);
        setError("Authentication failed.");
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      });
  }, [exchangeSessionId, location.hash, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-8">
        <div>
          <p className="text-destructive">{error}</p>
          <p className="text-muted-foreground text-sm mt-2">Redirecting…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent mx-auto" />
        <p className="mt-4 font-serif text-primary">Signing you in…</p>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/" replace />;
  return children;
}
